import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { Form } from '@unform/web';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import qs from 'querystring';
import { isArray } from 'lodash';

import { Spinner } from '~/components';
import { Input, Autocomplete } from '~/components/Form';
import { Select } from '~/components/Form/v2';
import { t } from '~/helpers';
import {
  storeData,
  updateData,
} from '~/store/modules/vazaoEquipamentosBicos/actions';
import api from '~/services/api';

import * as S from './styles';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const [nozzle, setNozzle] = useState([]);
  const [vehicle, setVehicle] = useState([]);

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.vazaoEquipamentosBicos
  );

  const initials = create ? data : { ...find, ...data };

  const handleGetAPINozzle = useCallback(async (id) => {
    try {
      const response = await api.get('bicosposto', {
        params: {
          wherefield: ['post_cod_posto'],
          valuewherefield: [id],
          typewherefield: ['EQUALS'],
          pagesize: 99999,
          pesquisalista: true,
        },
        paramsSerializer: (p) => qs.stringify(p),
      });

      setNozzle(
        response.data.items.map((i) => ({
          label: i.bico_cod_bico,
          value: i.bico_cod_bico,
        }))
      );
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  const handleGetAPIVehicle = useCallback(async (id) => {
    try {
      const response = await api.get('veiculo', {
        params: {
          wherefield: ['empe_cod_emp'],
          valuewherefield: [id],
          typewherefield: ['EQUALS'],
          pagesize: 99999,
          pesquisalista: true,
        },
        paramsSerializer: (p) => qs.stringify(p),
      });

      setVehicle(
        response.data.items.map((i) => ({
          label: i.veic_cod_veic,
          value: i.veic_cod_veic,
        }))
      );
    } catch (err) {
      if (isArray(err)) err.map((e) => toast.error(t(e)));
      else toast.error(t(err));
    }
  }, []);

  const handleSelectOptionTank = useCallback(
    (_, reason) => {
      if (reason === 'select-option') {
        setNozzle([]);
        reference.current.setFieldValue('bico_cod_bico', '');
      }
    },
    [reference]
  );

  const handleSelectOptionCompany = useCallback(
    (_, reason) => {
      if (reason === 'select-option') {
        setVehicle([]);
        reference.current.setFieldValue('veic_cod_veic', '');
      }
    },
    [reference]
  );

  const handleChangeTank = useCallback((value) => {
    if (!value.post_cod_posto) {
      handleSelectOptionTank(undefined, 'select-option');
    } else {
      handleGetAPINozzle(value.post_cod_posto);
    }
  }, []);

  const handleChangeCompany = useCallback((value) => {
    if (!value.empe_cod_emp) {
      handleSelectOptionCompany(undefined, 'select-option');
    } else {
      handleGetAPIVehicle(value.empe_cod_emp);
    }
  }, []);

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        post_cod_posto: Yup.string()
          .typeError('Campo Obrigatório')
          .required('Campo Obrigatório'),
        bico_cod_bico: Yup.string()
          .typeError('Campo Obrigatório')
          .required('Campo Obrigatório'),
        empe_cod_emp: Yup.string()
          .typeError('Campo Obrigatório')
          .required('Campo Obrigatório'),
        veic_cod_veic: Yup.string()
          .typeError('Campo Obrigatório')
          .required('Campo Obrigatório'),
        vpbe_vazao_maxima: Yup.string()
          .typeError('Campo Obrigatório')
          .required('Campo Obrigatório'),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        vpbe_datahora_atu: '',
        vpbe_st_reg: '',
        vpbe_st_trans: '',
        ...find,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeData(objSend, params));
      } else {
        await dispatch(updateData(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  if (loading)
    return (
      <div style={{ height: '14rem', width: '50rem' }}>
        <Spinner loading />
      </div>
    );

  return (
    <S.Wrapper ref={reference} initialData={initials} onSubmit={handleSubmit}>
      <Autocomplete
        name="post_cod_posto"
        label="post_desc"
        url="usuarioposto/inversor"
        unique="post_cod_posto"
        title="Posto"
        getValue={handleChangeTank}
        onClose={handleSelectOptionTank}
        disabled={!create}
      />

      <Select
        name="bico_cod_bico"
        label="Bico"
        data={nozzle}
        disabled={!create}
      />

      <Autocomplete
        name="empe_cod_emp"
        label="empe_desc"
        url="empresa"
        unique="empe_cod_emp"
        title="Empresa"
        getValue={handleChangeCompany}
        onClose={handleSelectOptionCompany}
        disabled={!create}
      />

      <Select
        name="veic_cod_veic"
        label="Equipamento"
        data={vehicle}
        disabled={!create}
      />

      <Input
        name="vpbe_vazao_maxima"
        label="Vazão Máxima (L/min)"
        type="number"
      />
    </S.Wrapper>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
